import { listRowKey, listRows } from "../listRows";
import {
  basicAuth,
  bearerAuth,
  headerAuth,
  queryAuth,
} from "../standardAuthPatterns";
import { type AuthBuilderInput, defineConnector } from "../types";
import logo from "./logo.svg";

const authMethodTarget = {
  kind: "configuration" as const,
  field: "authMethod",
} as const;

const extraEntries = (app: AuthBuilderInput, prefix: string) =>
  listRows(app.configuration, prefix)
    .filter((row) => row.values.name)
    .map((row) => {
      const valueKey = listRowKey(prefix, row.id, "value");
      return {
        name: row.values.name!,
        // The value's location is the secret/plain flag: present in
        // configuration means the user opted out of encryption.
        value: (row.values.value !== undefined
          ? app.config[valueKey]
          : app.secret[valueKey])!,
      };
    });

export const apiKey = defineConnector({
  identifier: "apiKey",
  name: "API key",
  tagLine: "Connect to anything",
  logo,
  protocol: "http",
  tags: ["custom"],
  defaultUrlPattern: () => ".*",
  customizeStepHandledInline: true,
  auth: (app) => {
    const apiKeyRef = app.secret.apiKey;
    const method =
      app.configuration.authMethod ??
      (app.configuration.header ? "header" : undefined) ??
      (app.configuration.query ? "query" : undefined) ??
      "bearer";
    switch (method) {
      case "basic": {
        const extra = extraEntries(app, "headers");
        const base = basicAuth({
          username: app.config.username,
          password: apiKeyRef,
        });
        return extra.length ? base.withHeaders(extra) : base;
      }
      case "header":
        return headerAuth(
          [
            {
              name: app.configuration.header ?? "",
              value: apiKeyRef,
              scheme: app.configuration.scheme,
            },
            ...extraEntries(app, "headers"),
          ].filter((header) => header.name)
        );
      case "query":
        return queryAuth(
          [
            { name: app.configuration.query ?? "", value: apiKeyRef },
            ...extraEntries(app, "queryParams"),
          ].filter((param) => param.name)
        );
      default:
        return bearerAuth(apiKeyRef);
    }
  },
  steps: [
    {
      title: "Configure your API key connection",
      items: [
        {
          kind: "input",
          label: "Name",
          placeholder: "e.g. Stripe, Twilio",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "url-pattern",
          placeholder: "api.example.com",
          target: { kind: "connectedApp", field: "urlPattern" },
        },
        {
          kind: "select",
          label: "Authentication method",
          target: authMethodTarget,
          defaultValue: "bearer",
          options: [
            { value: "bearer", label: "Bearer token" },
            { value: "header", label: "Custom headers" },
            { value: "basic", label: "Basic authentication" },
            { value: "query", label: "Query parameters" },
          ],
          helperText: "How the credential is sent with each request.",
        },
        {
          kind: "input",
          label: "Username",
          placeholder: "username",
          target: { kind: "configuration", field: "username" },
          visibleWhen: { target: authMethodTarget, value: "basic" },
        },
        {
          kind: "input",
          label: "API key",
          placeholder: "secret",
          target: { kind: "secrets", field: "apiKey" },
          visibleWhen: { target: authMethodTarget, value: "bearer" },
        },
        {
          kind: "input",
          label: "Password",
          placeholder: "password",
          target: { kind: "secrets", field: "apiKey" },
          visibleWhen: { target: authMethodTarget, value: "basic" },
        },
        {
          kind: "list",
          label: "Additional headers",
          prefix: "headers",
          addLabel: "Add header",
          columns: [
            { field: "name", placeholder: "X-Auth-Token" },
            {
              field: "value",
              placeholder: "plain text",
              secretPlaceholder: "secret",
              secret: true,
            },
          ],
          visibleWhen: { target: authMethodTarget, value: "basic" },
        },
        {
          kind: "list",
          label: "Headers",
          prefix: "headers",
          addLabel: "Add header",
          primaryRow: {
            name: {
              target: { kind: "configuration", field: "header" },
              placeholder: "X-API-Key",
            },
            value: {
              target: { kind: "secrets", field: "apiKey" },
              placeholder: "secret",
            },
          },
          columns: [
            { field: "name", placeholder: "X-Auth-Token" },
            {
              field: "value",
              placeholder: "plain text",
              secretPlaceholder: "secret",
              secret: true,
            },
          ],
          visibleWhen: { target: authMethodTarget, value: "header" },
        },
        {
          kind: "input",
          label: "Scheme prefix",
          placeholder: "e.g. Token, Api-Key",
          helperText:
            "Optional. Prepended to the first header’s value, e.g. “Token <key>”.",
          target: { kind: "configuration", field: "scheme" },
          visibleWhen: { target: authMethodTarget, value: "header" },
        },
        {
          kind: "list",
          label: "Query parameters",
          prefix: "queryParams",
          addLabel: "Add parameter",
          primaryRow: {
            name: {
              target: { kind: "configuration", field: "query" },
              placeholder: "e.g. api_key",
            },
            value: {
              target: { kind: "secrets", field: "apiKey" },
              placeholder: "secret",
            },
          },
          columns: [
            { field: "name", placeholder: "e.g. token" },
            {
              field: "value",
              placeholder: "plain text",
              secretPlaceholder: "secret",
              secret: true,
            },
          ],
          visibleWhen: { target: authMethodTarget, value: "query" },
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Optional. Upload a PEM CA certificate to trust a private or self-signed server.",
          target: { kind: "configuration", field: "caCertificate" },
        },
        {
          kind: "test-url",
          label: "Test URL",
          placeholder: "https://api.example.com/user",
          helperText: "Leave blank to skip testing.",
          urlTarget: { kind: "configuration", field: "testUrl" },
          methodTarget: { kind: "configuration", field: "testMethod" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ testUrl, testMethod }) => ({
      url: testUrl ?? "",
      method: testMethod === "POST" ? "POST" : "GET",
    }),
  },
});
